import { useCreateProjectMutation, useUpdateProjectMutation } from 'api/api';
import { ProjectContext } from 'context/ProjectContext';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project, User } from 'types/types';

interface HookProps {
  userData: User | undefined
}

// isSubmitting and leaveForm are optional parameters used to indicate if the hook should submit the form and leave the form page, respectively
interface UpdaterProps {
  isSubmitting?: boolean
  formValues?: Partial<Project>
  leaveForm?: boolean
}

// TODO: There should be a check to see if data has been updated before sending redundant update requests
export const useCreateUpdateProject = ({ userData }: HookProps) => {
  const navigate = useNavigate();
  const [createProject, { error: createError, isLoading: isCreating }] = useCreateProjectMutation();
  const [updateProject, { error: updateError, isLoading: isUpdating }] = useUpdateProjectMutation();

  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('ProjectContext error');
  }
  const { setProjectId, projectDetails, setProjectDetails, isNewProject } = context;

  // Helper function updates ProjectContext with calculated values
  const updateContextWithResponse = (response: Project) => {
    setProjectDetails(prevDetails => ({
      ...prevDetails,
      biogenic_carbon_content: response.biogenic_carbon_content,
      total_gwp_kg_exclude_d: response.total_gwp_kg_exclude_d,
      total_intensity_exclude_d: response.total_intensity_exclude_d,
    }));
  };

  const handleCreateUpdateProject = async ({
    formValues = {}, 
    isSubmitting = false, 
    leaveForm = true
  }: UpdaterProps) => {
    if (!userData) {
      console.error("Error fetching user data, unable to save project.");
      return;
    }

    const updaterIdentifier = `${userData?.first_name} ${userData?.last_name}`;

    let updatedProjectDetails: Project = {
      ...projectDetails,
      ...formValues,
      updated_at: new Date().toISOString(),
      updated_by: updaterIdentifier,
    };

    if (isSubmitting) {
      updatedProjectDetails = {
        ...updatedProjectDetails,
        is_submitted: true,
        submitted_at: new Date().toISOString(),
        submitted_by: updaterIdentifier
      };
    }

    try {
      let response: Project;
      
      if (updatedProjectDetails.id === -1) {
        const newProject = {
          ...updatedProjectDetails,
          created_at: new Date().toISOString(),
          created_by: updaterIdentifier,
        };
        delete (newProject as Partial<Project>).id;
        
        response = await createProject(newProject).unwrap();
        setProjectId(response.id);
        updateContextWithResponse(response);
        leaveForm && navigate('/projects?modal=open&modalType=addProjectSuccess');
      } else {
        response = await updateProject(updatedProjectDetails).unwrap();
        updateContextWithResponse(response);
        if (isNewProject) {
          leaveForm && navigate('/projects?modal=open&modalType=addProjectSuccess');
        } else {
          leaveForm && navigate('/projects?modal=open&modalType=updateProjectSuccess');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };


  const error = createError || updateError;
  const isLoading = isCreating || isUpdating;

  return { 
    handleCreateUpdateProject,
    error,
    isLoading
  };
};